// /admin — operator-facing console for the contact submissions captured by the
// public site. This is a TOP-LEVEL route OUTSIDE the bilingual /:lang group: it is
// internal tooling, not part of the public site, so labels are Chinese-only and it
// deliberately does NOT use i18n.
//
// Flow:
//   * On mount we call getSession(). Until that resolves we show a spinner.
//   * authenticated:false  -> centered LOGIN CARD (username + password).
//   * authenticated:true   -> DASHBOARD (filter + table + row actions).
//   * Any protected call that comes back 401 (session expired/lost) bounces the
//     operator straight back to the login card. 503 surfaces "未启用".
//
// Styling reuses the ContactForm input/button language (brand tokens + Tailwind).
import { useCallback, useEffect, useRef, useState } from "react";

import {
  getSession,
  login,
  logout,
  listRequests,
  setHandled,
  deleteRequest,
  listIssues,
  createIssue,
  updateIssue,
  deleteIssue,
  getAnalytics,
  uploadIssueAttachment,
  fetchAttachmentObjectUrl,
  deleteIssueAttachment,
  downloadGuidePdf,
  listLoginAudit,
  ATTACHMENT_TYPES,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_COUNT,
  type AdminRequest,
  type RequestStatus,
  type AdminIssue,
  type IssueStatus,
  type IssueFilter,
  type IssueAttachment,
  type AdminAnalytics,
  type LoginAuditEvent,
  type LoginAuditFilter,
  type LoginAuditOutcome,
} from "../api/admin";
import { countryLabel, cityLabel, regionLabel } from "../lib/geoNames";

// --- shared style fragments (mirrors ContactForm) ----------------------------
const INPUT_CLASS =
  "w-full rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white px-3.5 py-2.5 text-[var(--text-base)] text-[var(--ink)] placeholder:text-[var(--brand-slate-text)] transition-colors focus:outline-none focus-visible:border-[var(--brand-azure)] focus-visible:ring-2 focus-visible:ring-[var(--brand-tint)] focus-visible:ring-offset-1";
const PRIMARY_BTN =
  "inline-flex items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-primary)] px-6 py-2.5 text-[var(--text-base)] font-medium text-white shadow-[var(--shadow-card)] transition-colors hover:bg-[var(--brand-azure)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)] disabled:cursor-not-allowed disabled:opacity-60";
const GHOST_BTN =
  "inline-flex items-center justify-center rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white px-4 py-2 text-[var(--text-sm)] font-medium text-[var(--brand-azure-text)] transition-colors hover:border-[var(--brand-azure)] hover:bg-[var(--brand-tint-3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)] disabled:cursor-not-allowed disabled:opacity-60";
const DANGER_BTN =
  "inline-flex items-center justify-center rounded-[var(--radius-md)] border border-red-200 bg-white px-4 py-2 text-[var(--text-sm)] font-medium text-red-600 transition-colors hover:border-red-400 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400 disabled:cursor-not-allowed disabled:opacity-60";

function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  );
}

// --- list paging + search helpers (shared by RequestsView + IssuesView) -------
const PAGE_SIZE = 20;

/** Debounce any fast-changing value (used for the search box -> q). */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Destructive-action confirm. Kept centralised so every delete uses the same
 * reversible-by-dev wording. window.confirm is acceptable per the spec.
 */
function confirmDelete(message: string): boolean {
  return window.confirm(message);
}

/** Human-readable file size (e.g. "1.4 MB"). */
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Client-side image guard mirroring the BFF attachment contract: <=3MB and one
 * of the allowed image types. Returns a Chinese error string, or null when OK.
 */
function validateImageFile(file: File): string | null {
  if (!(ATTACHMENT_TYPES as readonly string[]).includes(file.type)) {
    return "仅支持 PNG / JPEG / WebP / GIF 图片";
  }
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return `图片过大（${formatBytes(file.size)}），单张需 ≤ 3MB`;
  }
  return null;
}

/** SearchBox — controlled text input with a clear (×) button; brand styling. */
function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <svg
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--brand-slate-text)]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-56 rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white py-2 pl-9 pr-8 text-[var(--text-sm)] text-[var(--ink)] placeholder:text-[var(--brand-slate-text)] transition-colors focus:outline-none focus-visible:border-[var(--brand-azure)] focus-visible:ring-2 focus-visible:ring-[var(--brand-tint)]"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="清除搜索"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-[var(--brand-slate-text)] hover:bg-[var(--brand-tint-3)] hover:text-[var(--brand-azure-text)]"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Pager — 上一页 / 下一页 + "第 X / Y 页 · 共 N 条". Disabled at the ends. Pages
 * are 1-based; totalPages is derived from total/pageSize (>= 1).
 */
function Pager({
  page,
  pageSize,
  total,
  onPage,
  disabled,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (next: number) => void;
  disabled?: boolean;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const clamped = Math.min(Math.max(1, page), totalPages);
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
      <span className="text-[var(--text-sm)] text-[var(--brand-slate-text)]">
        第 {clamped} / {totalPages} 页 · 共 {formatCount(total)} 条
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={GHOST_BTN}
          disabled={disabled || clamped <= 1}
          onClick={() => onPage(clamped - 1)}
        >
          上一页
        </button>
        <button
          type="button"
          className={GHOST_BTN}
          disabled={disabled || clamped >= totalPages}
          onClick={() => onPage(clamped + 1)}
        >
          下一页
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  // null = still resolving the initial session check.
  const [authed, setAuthed] = useState<boolean | null>(null);

  const refreshSession = useCallback(async () => {
    const { data, networkError } = await getSession();
    // On network error keep the operator on the login card (safe default).
    setAuthed(!networkError && !!data.authenticated);
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--brand-tint-3)]/40 text-[var(--brand-slate-text)]">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  return authed ? (
    <Dashboard onSignedOut={() => setAuthed(false)} />
  ) : (
    <LoginCard onAuthed={() => setAuthed(true)} />
  );
}

// ---------------------------------------------------------------------------
// LoginCard — centered username/password card.
// ---------------------------------------------------------------------------
function LoginCard({ onAuthed }: { onAuthed: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      const { status, networkError } = await login(username, password);
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return;
      }
      if (status === 200) {
        onAuthed();
        return;
      }
      if (status === 429) {
        setError("尝试过多，请稍后再试");
      } else if (status === 503) {
        setError("管理后台未启用");
      } else {
        // 401 and anything else: generic credentials message (never distinguish).
        setError("用户名或密码错误");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--brand-tint-3)]/40 px-6 py-12">
      <form
        onSubmit={handleSubmit}
        noValidate
        className="w-full max-w-sm rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-8 shadow-[var(--shadow-card)]"
      >
        <h1 className="font-zh text-[var(--text-2xl)] font-bold text-[var(--ink)]">管理后台登录</h1>
        <p className="mt-2 text-[var(--text-sm)] text-[var(--brand-slate-text)]">
          请使用管理员账号登录以查看联系表单。
        </p>

        {error && (
          <p
            className="mt-5 rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-4 py-3 text-[var(--text-sm)] font-medium text-red-700"
            role="alert"
          >
            {error}
          </p>
        )}

        <div className="mt-6 space-y-4">
          <div>
            <label htmlFor="admin-user" className="mb-1.5 block text-[var(--text-sm)] font-medium text-[var(--ink)]">
              用户名
            </label>
            <input
              id="admin-user"
              name="username"
              type="text"
              autoComplete="username"
              className={INPUT_CLASS}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="admin-pass" className="mb-1.5 block text-[var(--text-sm)] font-medium text-[var(--ink)]">
              密码
            </label>
            <input
              id="admin-pass"
              name="password"
              type="password"
              autoComplete="current-password"
              className={INPUT_CLASS}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
        </div>

        <button type="submit" className={`mt-6 w-full ${PRIMARY_BTN}`} disabled={pending}>
          {pending && <Spinner className="-ml-1 mr-2.5 h-4 w-4" />}
          {pending ? "登录中…" : "登录"}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard — filter + requests table + row actions.
// ---------------------------------------------------------------------------
const FILTERS: { value: RequestStatus; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "unhandled", label: "未处理" },
  { value: "handled", label: "已处理" },
];

type TabKey = "dashboard" | "requests" | "issues" | "audit";
const TABS: { value: TabKey; label: string }[] = [
  // 数据看板 is the FIRST / DEFAULT tab — admins land on the overview after login.
  { value: "dashboard", label: "数据看板" },
  { value: "requests", label: "联系请求" },
  { value: "issues", label: "问题跟踪" },
  { value: "audit", label: "登录记录" },
];

function Dashboard({ onSignedOut }: { onSignedOut: () => void }) {
  const [tab, setTab] = useState<TabKey>("dashboard");
  const [guideDownloading, setGuideDownloading] = useState(false);

  async function handleLogout() {
    await logout();
    onSignedOut();
  }

  return (
    <div className="min-h-screen bg-[var(--brand-tint-3)]/30">
      <header className="border-b border-[var(--brand-tint)] bg-white">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-4 px-6 py-5">
          <div>
            <h1 className="font-zh text-[var(--text-xl)] font-bold text-[var(--ink)]">网站管理后台</h1>
            <p className="mt-0.5 text-[var(--text-sm)] text-[var(--brand-slate-text)]">查看联系请求与跟踪网站问题</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Latest admin guide. Fetched with the CSRF header (the /api gate requires
                it on every method) then downloaded as a blob — a plain <a download> 403s. */}
            <button
              type="button"
              className={GHOST_BTN}
              disabled={guideDownloading}
              onClick={async () => {
                setGuideDownloading(true);
                const r = await downloadGuidePdf();
                setGuideDownloading(false);
                if (!r.ok) window.alert("下载失败,请重试。");
              }}
            >
              {guideDownloading ? "下载中…" : "下载使用说明 PDF"}
            </button>
            <button type="button" className={GHOST_BTN} onClick={handleLogout}>
              退出登录
            </button>
          </div>
        </div>
        {/* Tab switcher — sits in the header, below the title row. */}
        <div className="mx-auto w-full max-w-6xl px-6">
          <nav className="-mb-px flex gap-1" role="tablist" aria-label="管理视图">
            {TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={tab === t.value}
                onClick={() => setTab(t.value)}
                className={[
                  "border-b-2 px-4 py-2.5 text-[var(--text-sm)] font-medium transition-colors",
                  tab === t.value
                    ? "border-[var(--brand-primary)] text-[var(--brand-azure-text)]"
                    : "border-transparent text-[var(--brand-slate-text)] hover:text-[var(--brand-azure-text)]",
                ].join(" ")}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {tab === "dashboard" ? (
        <DashboardView onSignedOut={onSignedOut} />
      ) : tab === "requests" ? (
        <RequestsView onSignedOut={onSignedOut} />
      ) : tab === "issues" ? (
        <IssuesView onSignedOut={onSignedOut} />
      ) : (
        <LoginAuditView onSignedOut={onSignedOut} />
      )}
    </div>
  );
}

// ===========================================================================
// DashboardView — 数据看板: visitor + geo overview (the default tab).
//
// Reads GET /api/admin/analytics (always well-formed; zeros / empty arrays on
// no data). All charts are inline SVG / CSS — no external chart library. Fetches
// on mount and on 刷新; bounces to login on 401, surfaces 未启用 on 503.
// ===========================================================================
function DashboardView({ onSignedOut }: { onSignedOut: () => void }) {
  const [data, setData] = useState<AdminAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { status: code, data: payload, networkError } = await getAnalytics();
    if (networkError) {
      setError("网络连接失败，请稍后重试");
      setLoading(false);
      return;
    }
    if (code === 401) {
      onSignedOut();
      return;
    }
    if (code === 503) {
      setError("管理后台未启用");
      setLoading(false);
      return;
    }
    if (code !== 200) {
      setError("加载失败，请稍后重试");
      setLoading(false);
      return;
    }
    setData(payload);
    setLoading(false);
  }, [onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  const generatedAt = data?.generatedAt ? formatDate(data.generatedAt) : null;
  // "数据较少" hint: no visitors recorded in the last 30 days at all.
  const isSparse =
    !!data && data.uniqueVisitors.d30 === 0 && data.pageviews.d30 === 0;

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-8">
      {/* Title row + generated-at + refresh */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-zh text-[var(--text-base)] font-semibold text-[var(--ink)]">
            访问概览 Overview
          </h2>
          <p className="mt-0.5 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
            {generatedAt ? `数据生成时间 ${generatedAt}` : "独立访客与地理分布"}
          </p>
        </div>
        <button type="button" className={GHOST_BTN} onClick={() => void load()} disabled={loading}>
          {loading && <Spinner className="-ml-0.5 mr-2 h-3.5 w-3.5" />}
          刷新
        </button>
      </div>

      {/* Plain-language accuracy note. */}
      <p className="mb-5 rounded-[var(--radius-md)] border border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/50 px-4 py-3 text-[var(--text-xs)] leading-relaxed text-[var(--brand-slate-text)]">
        说明：以上数据为近实时的近似统计（独立访客采用概率去重算法，存在小幅误差），
        网站刚上线时样本较少、波动较大，仅供参考。
        <br />
        <span className="text-[var(--brand-slate-text)]/80">
          Figures are approximate and near-real-time; data is sparse and noisy right after launch.
        </span>
      </p>

      {error && (
        <p
          className="mb-5 rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-4 py-3 text-[var(--text-sm)] font-medium text-red-700"
          role="alert"
        >
          {error}
        </p>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center gap-3 rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-[var(--brand-slate-text)]">
          <Spinner className="h-5 w-5" />
          <span className="text-[var(--text-sm)]">加载中…</span>
        </div>
      ) : !data ? (
        <div className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-center">
          <p className="text-[var(--text-base)] font-medium text-[var(--ink)]">暂无数据</p>
          <p className="mt-1 text-[var(--text-sm)] text-[var(--brand-slate-text)]">
            还没有可展示的访问数据。
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {isSparse && (
            <p className="rounded-[var(--radius-md)] border border-amber-200 bg-amber-50 px-4 py-3 text-[var(--text-sm)] font-medium text-amber-800">
              数据较少：近 30 天暂未记录到访问，下面的图表会在有访客后逐步填充。
            </p>
          )}

          {/* Stat cards — 独立访客 + 浏览量, each across 今日 / 近7天 / 近30天. */}
          <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <StatGroup title="独立访客" subtitle="Unique visitors" window={data.uniqueVisitors} />
            <StatGroup title="浏览量" subtitle="Page views" window={data.pageviews} />
          </section>

          {/* 访问趋势 — 14-day inline-SVG bar chart. */}
          <section className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6 shadow-[var(--shadow-card)]">
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <h3 className="font-zh text-[var(--text-base)] font-semibold text-[var(--ink)]">访问趋势</h3>
              <span className="text-[var(--text-xs)] text-[var(--brand-slate-text)]">近 14 天 · Last 14 days</span>
            </div>
            <p className="mb-4 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
              柱状为浏览量，点线为独立访客
            </p>
            <TrendChart trend={data.trend} />
          </section>

          {/* Geo breakdowns — countries + cities, side by side on wide screens. */}
          <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <BarList
              title="国家 / 地区分布"
              subtitle="近 30 天 · Top countries"
              emptyLabel="暂无地理数据"
              rows={data.topCountries.map((c) => ({
                key: c.country,
                label: countryLabel(c.country),
                count: c.count,
                pct: c.pct,
              }))}
            />
            <BarList
              title="城市分布"
              subtitle="近 30 天 · Top cities"
              emptyLabel="暂无城市数据"
              rows={data.topCities.map((c) => ({
                key: `${c.country}|${c.region}|${c.city}`,
                label: cityLabel(c.city),
                sub: [countryLabel(c.country), regionLabel(c.region)]
                  .filter(Boolean)
                  .join(" · "),
                count: c.count,
                pct: c.pct,
              }))}
            />
          </section>
        </div>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// StatGroup — a card holding one metric across three windows (今日/近7天/近30天).
// ---------------------------------------------------------------------------
function StatGroup({
  title,
  subtitle,
  window,
}: {
  title: string;
  subtitle: string;
  window: AdminAnalytics["uniqueVisitors"];
}) {
  const cells: { label: string; value: number }[] = [
    { label: "今日", value: window.d1 },
    { label: "近7天", value: window.d7 },
    { label: "近30天", value: window.d30 },
  ];
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6 shadow-[var(--shadow-card)]">
      <div className="mb-4 flex items-baseline gap-2">
        <h3 className="font-zh text-[var(--text-base)] font-semibold text-[var(--ink)]">{title}</h3>
        <span className="text-[var(--text-xs)] text-[var(--brand-slate-text)]">{subtitle}</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {cells.map((c) => (
          <div
            key={c.label}
            className="rounded-[var(--radius-md)] border border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/40 px-3 py-3 text-center"
          >
            <div className="text-[var(--text-2xl)] font-bold leading-tight text-[var(--brand-azure-text)]">
              {formatCount(c.value)}
            </div>
            <div className="mt-1 text-[var(--text-xs)] text-[var(--brand-slate-text)]">{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrendChart — inline SVG: pageview BARS (the "PV graph") + a uniqueVisitors
// point-line overlay, with a real left Y axis (numeric ticks + gridlines), a
// reliable hover/tap tooltip (full-height hit-rects, NOT SVG <title>), per-bar
// value labels when there's room, and a peak label on narrow/mobile. Pure SVG
// + a little React state — no chart library. Brand azure #00A8E4.
//
// IMPORTANT: both axes (bars + the UV line) share ONE scale (niceMax of the
// pageviews max). Pageviews >= uniqueVisitors per day, so the line always sits
// at/under the bars and the single labelled Y axis reads correctly for both.
// ---------------------------------------------------------------------------

/** Round a raw max up to a "nice" axis ceiling (1/2/5 × 10^k). Always >= 1. */
function niceMax(raw: number): number {
  if (raw <= 0) return 1;
  const exp = Math.floor(Math.log10(raw));
  const pow = Math.pow(10, exp);
  const frac = raw / pow;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * pow;
}

/** Evenly spaced integer tick values 0..max (inclusive), `count` intervals. */
function axisTicks(max: number, count: number): number[] {
  const ticks: number[] = [];
  for (let i = 0; i <= count; i++) ticks.push(Math.round((max / count) * i));
  // De-dupe in case rounding collapses small ranges (e.g. max=1).
  return Array.from(new Set(ticks));
}

function TrendChart({ trend }: { trend: AdminAnalytics["trend"] }) {
  // Which day's tooltip is showing (index into trend), or null. Driven by the
  // invisible hit-rects below — works for both mouse hover and touch/tap.
  const [active, setActive] = useState<number | null>(null);

  if (!trend.length) {
    return (
      <p className="py-8 text-center text-[var(--text-sm)] text-[var(--brand-slate-text)]">暂无趋势数据</p>
    );
  }

  // --- geometry (viewBox units; scales to container via preserveAspectRatio) ---
  const W = 720;
  const H = 240;
  const padL = 44; // room for the Y-axis tick labels
  const padR = 10;
  const padT = 16;
  const padB = 34; // room for the date labels
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = trend.length;
  const slot = plotW / n;
  const barW = Math.max(5, Math.min(28, slot * 0.55));

  // Shared scale: round the pageviews peak up to a nice ceiling. UV uses the
  // SAME scale (UV <= PV daily), so the one Y axis is correct for both series.
  const rawMax = Math.max(...trend.map((d) => Math.max(d.pageviews, d.uniqueVisitors)));
  const maxY = niceMax(Math.max(1, rawMax));
  const ticks = axisTicks(maxY, 4);

  const xCenter = (i: number) => padL + slot * i + slot / 2;
  const yOf = (v: number) => padT + plotH - (v / maxY) * plotH;
  const baseY = padT + plotH;

  const uvPath = trend
    .map((d, i) => `${i === 0 ? "M" : "L"} ${xCenter(i).toFixed(1)} ${yOf(d.uniqueVisitors).toFixed(1)}`)
    .join(" ");

  // Date label density: every other day once crowded; always show first/last.
  const labelStep = n > 8 ? 2 : 1;
  // Per-bar value labels need horizontal room; only when slots are wide enough.
  const showBarValues = slot >= 38;
  // The peak pageviews day (for the always-on mobile annotation).
  let peakIdx = 0;
  for (let i = 1; i < n; i++) if (trend[i].pageviews > trend[peakIdx].pageviews) peakIdx = i;

  const short = (date: string) => (date.length >= 10 ? date.slice(5) : date);
  const activeDay = active != null ? trend[active] : null;

  return (
    <div className="w-full">
      {/* viewBox + preserveAspectRatio="xMidYMid meet" => responsive to ~320px
          with NO horizontal scroll; the axis/gridlines/labels scale together. */}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-60 w-full"
        role="img"
        aria-label={`近 ${n} 天访问趋势：浏览量与独立访客`}
        preserveAspectRatio="xMidYMid meet"
        onMouseLeave={() => setActive(null)}
      >
        {/* --- Y axis: gridlines + numeric tick labels --- */}
        {ticks.map((t) => {
          const y = yOf(t);
          return (
            <g key={`tick-${t}`}>
              <line
                x1={padL}
                y1={y}
                x2={W - padR}
                y2={y}
                stroke="var(--brand-tint-2)"
                strokeWidth={t === 0 ? 1 : 0.75}
                opacity={t === 0 ? 0.9 : 0.5}
              />
              <text
                x={padL - 8}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={12}
                fill="var(--brand-slate-text)"
              >
                {formatCount(t)}
              </text>
            </g>
          );
        })}

        {/* --- pageview bars (the PV graph) + per-bar value labels --- */}
        {trend.map((d, i) => {
          const x = padL + slot * i + (slot - barW) / 2;
          const y = yOf(d.pageviews);
          const h = Math.max(0, baseY - y);
          const isActive = active === i;
          return (
            <g key={`bar-${d.date}`}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                rx={2}
                fill="var(--brand-primary)"
                opacity={active == null || isActive ? 0.9 : 0.45}
              />
              {showBarValues && d.pageviews > 0 && (
                <text
                  x={xCenter(i)}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="var(--brand-primary-text)"
                >
                  {d.pageviews}
                </text>
              )}
            </g>
          );
        })}

        {/* --- uniqueVisitors overlay (line + dots), SAME scale as the bars --- */}
        <path d={uvPath} fill="none" stroke="var(--brand-azure-text)" strokeWidth={1.75} opacity={0.95} />
        {trend.map((d, i) => (
          <circle
            key={`uv-${d.date}`}
            cx={xCenter(i)}
            cy={yOf(d.uniqueVisitors)}
            r={active === i ? 3.75 : 2.75}
            fill="var(--brand-azure-text)"
          />
        ))}

        {/* --- date labels along the baseline --- */}
        {trend.map((d, i) =>
          i % labelStep === 0 || i === n - 1 ? (
            <text
              key={`date-${d.date}`}
              x={xCenter(i)}
              y={H - 10}
              textAnchor="middle"
              fontSize={11}
              fill="var(--brand-slate-text)"
            >
              {short(d.date)}
            </text>
          ) : null,
        )}

        {/* --- peak annotation: never rely on hover for the headline number ---
            Only when no tooltip is open and the chart isn't dense with values. */}
        {!showBarValues && active == null && trend[peakIdx].pageviews > 0 && (
          <text
            x={Math.min(W - padR, Math.max(padL + 4, xCenter(peakIdx)))}
            y={Math.max(padT + 10, yOf(trend[peakIdx].pageviews) - 6)}
            textAnchor="middle"
            fontSize={11}
            fontWeight={700}
            fill="var(--brand-primary-text)"
          >
            峰值 {trend[peakIdx].pageviews}
          </text>
        )}

        {/* --- active-day guide line (subtle) --- */}
        {active != null && (
          <line
            x1={xCenter(active)}
            y1={padT}
            x2={xCenter(active)}
            y2={baseY}
            stroke="var(--brand-azure-text)"
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.5}
          />
        )}

        {/* --- invisible full-height hit-rects: one per day, for hover + tap --- */}
        {trend.map((d, i) => (
          <rect
            key={`hit-${d.date}`}
            x={padL + slot * i}
            y={padT}
            width={slot}
            height={plotH}
            fill="transparent"
            style={{ cursor: "pointer" }}
            onMouseEnter={() => setActive(i)}
            onMouseMove={() => setActive(i)}
            onClick={() => setActive((cur) => (cur === i ? null : i))}
          >
            {/* a11y fallback only (sighted users get the React tooltip below) */}
            <title>{`${d.date} · 浏览量 ${d.pageviews} · 独立访客 ${d.uniqueVisitors}`}</title>
          </rect>
        ))}
      </svg>

      {/* React tooltip — rendered in HTML (not SVG) so it's legible at any size
          and reliable on touch. Shows the active day's full numbers. */}
      <div className="mt-2 min-h-[1.5rem] text-center" aria-live="polite">
        {activeDay ? (
          <span className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-0.5 rounded-[var(--radius-md)] border border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/60 px-3 py-1 text-[var(--text-xs)] text-[var(--ink)]">
            <span className="font-medium">{activeDay.date}</span>
            <span>
              浏览量 <span className="font-semibold text-[var(--brand-primary-text)]">{activeDay.pageviews}</span>
            </span>
            <span>
              独立访客 <span className="font-semibold text-[var(--brand-azure-text)]">{activeDay.uniqueVisitors}</span>
            </span>
          </span>
        ) : (
          <span className="text-[var(--text-xs)] text-[var(--brand-slate-text)]">
            将鼠标移到柱状图上 / 点按查看每日数值
          </span>
        )}
      </div>

      {/* legend */}
      <div className="mt-2 flex items-center justify-center gap-5 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-[var(--brand-primary)]" />
          浏览量（柱）
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex h-2.5 w-4 items-center">
            <span className="block h-0.5 w-4 bg-[var(--brand-azure-text)]" />
          </span>
          独立访客（点线）
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BarList — a labelled list of horizontal CSS bars (geo breakdowns). Each row
// shows a name (+ optional subtext), a proportional bar, a count and a pct.
// ---------------------------------------------------------------------------
interface BarRow {
  key: string;
  label: string;
  sub?: string;
  count: number;
  pct: number;
}

function BarList({
  title,
  subtitle,
  rows,
  emptyLabel,
}: {
  title: string;
  subtitle: string;
  rows: BarRow[];
  emptyLabel: string;
}) {
  // Scale bars to the largest count so the leader fills the track.
  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6 shadow-[var(--shadow-card)]">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h3 className="font-zh text-[var(--text-base)] font-semibold text-[var(--ink)]">{title}</h3>
        <span className="text-[var(--text-xs)] text-[var(--brand-slate-text)]">{subtitle}</span>
      </div>
      {rows.length === 0 ? (
        <p className="py-8 text-center text-[var(--text-sm)] text-[var(--brand-slate-text)]">{emptyLabel}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => {
            const width = Math.max(2, Math.round((r.count / maxCount) * 100));
            return (
              <li key={r.key}>
                <div className="mb-1 flex items-baseline justify-between gap-3">
                  <div className="min-w-0">
                    <span className="text-[var(--text-sm)] font-medium text-[var(--ink)]">
                      {r.label || "未知"}
                    </span>
                    {r.sub && (
                      <span className="ml-2 truncate text-[var(--text-xs)] text-[var(--brand-slate-text)]">
                        {r.sub}
                      </span>
                    )}
                  </div>
                  <span className="whitespace-nowrap text-[var(--text-xs)] text-[var(--brand-slate-text)]">
                    {formatCount(r.count)} · {r.pct.toFixed(1)}%
                  </span>
                </div>
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-[var(--brand-tint-3)]"
                  role="progressbar"
                  aria-valuenow={r.pct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="h-full rounded-full bg-[var(--brand-primary)]"
                    style={{ width: `${width}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Group thousands for readability (e.g. 12,345). Locale-agnostic separators. */
function formatCount(n: number): string {
  return new Intl.NumberFormat("en-US").format(Math.max(0, Math.trunc(n)));
}

// ---------------------------------------------------------------------------
// RequestsView — filter + requests table + row actions (the original dashboard
// body, now scoped to its own tab).
// ---------------------------------------------------------------------------
function RequestsView({ onSignedOut }: { onSignedOut: () => void }) {
  const [status, setStatus] = useState<RequestStatus>("all");
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // search (debounced) + pagination state.
  const [search, setSearch] = useState("");
  const q = useDebounced(search, 350);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // IDs with an in-flight row action (toggle/delete), to disable their buttons.
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const setBusy = useCallback((id: string, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Reset to page 1 whenever the filter or the (debounced) query changes.
  useEffect(() => {
    setPage(1);
  }, [status, q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { status: code, data, networkError } = await listRequests(status, {
      page,
      pageSize: PAGE_SIZE,
      q,
    });
    if (networkError) {
      setError("网络连接失败，请稍后重试");
      setLoading(false);
      return;
    }
    if (code === 401) {
      // Session expired/lost -> bounce back to login.
      onSignedOut();
      return;
    }
    if (code === 503) {
      setError("管理后台未启用");
      setLoading(false);
      return;
    }
    if (code !== 200) {
      setError("加载失败，请稍后重试");
      setLoading(false);
      return;
    }
    const list = Array.isArray(data.requests) ? data.requests : [];
    setRequests(list);
    // Fall back to the page length when the server omits `total` (older BFF).
    setTotal(typeof data.total === "number" ? data.total : list.length);
    setLoading(false);
  }, [status, page, q, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleToggle(req: AdminRequest) {
    setBusy(req.id, true);
    try {
      const { status: code, networkError } = await setHandled(req.id, !req.handled);
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return;
      }
      if (code === 401) {
        onSignedOut();
        return;
      }
      if (code !== 200) {
        setError("操作失败，请稍后重试");
        return;
      }
      await load();
    } finally {
      setBusy(req.id, false);
    }
  }

  async function handleDelete(req: AdminRequest) {
    if (
      !confirmDelete(
        `确定删除来自「${req.name}」的留言吗？删除后将不再显示（如需恢复请联系开发）。`,
      )
    )
      return;
    setBusy(req.id, true);
    try {
      const { status: code, networkError } = await deleteRequest(req.id);
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return;
      }
      if (code === 401) {
        onSignedOut();
        return;
      }
      if (code !== 200) {
        setError("删除失败，请稍后重试");
        return;
      }
      // If we just emptied the last item on a non-first page, step back a page;
      // otherwise reload the current page in place.
      if (requests.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } finally {
      setBusy(req.id, false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-8">
      {/* Filter + search + count + refresh */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white p-1" role="tablist">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={status === f.value}
                  onClick={() => setStatus(f.value)}
                  className={[
                    "rounded-[var(--radius-sm)] px-4 py-1.5 text-[var(--text-sm)] font-medium transition-colors",
                    status === f.value
                      ? "bg-[var(--brand-primary)] text-white"
                      : "text-[var(--brand-slate-text)] hover:text-[var(--brand-azure-text)]",
                  ].join(" ")}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="搜索姓名 / 公司 / 邮箱 / 主题 / 内容"
            />
          </div>

          <div className="flex items-center gap-3">
            <span className="text-[var(--text-sm)] text-[var(--brand-slate-text)]">
              共 {formatCount(total)} 条
            </span>
            <button type="button" className={GHOST_BTN} onClick={() => void load()} disabled={loading}>
              {loading && <Spinner className="-ml-0.5 mr-2 h-3.5 w-3.5" />}
              刷新
            </button>
          </div>
        </div>

        {error && (
          <p
            className="mb-5 rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-4 py-3 text-[var(--text-sm)] font-medium text-red-700"
            role="alert"
          >
            {error}
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-3 rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-[var(--brand-slate-text)]">
            <Spinner className="h-5 w-5" />
            <span className="text-[var(--text-sm)]">加载中…</span>
          </div>
        ) : requests.length === 0 ? (
          <div className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-center">
            <p className="text-[var(--text-base)] font-medium text-[var(--ink)]">暂无留言</p>
            <p className="mt-1 text-[var(--text-sm)] text-[var(--brand-slate-text)]">
              {q
                ? "没有匹配搜索条件的留言。"
                : status === "all"
                  ? "还没有收到任何联系表单。"
                  : "当前筛选条件下没有记录。"}
            </p>
          </div>
        ) : (
          <>
            <RequestTable
              requests={requests}
              busyIds={busyIds}
              onToggle={handleToggle}
              onDelete={handleDelete}
            />
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={total}
              onPage={setPage}
              disabled={loading}
            />
          </>
        )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// RequestTable — responsive table (cards on small screens via the same markup).
// ---------------------------------------------------------------------------
function RequestTable({
  requests,
  busyIds,
  onToggle,
  onDelete,
}: {
  requests: AdminRequest[];
  busyIds: Set<string>;
  onToggle: (req: AdminRequest) => void;
  onDelete: (req: AdminRequest) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white shadow-[var(--shadow-card)]">
      <table className="w-full min-w-[60rem] border-collapse text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/50 text-[var(--brand-slate-text)]">
            <Th className="w-36">时间</Th>
            <Th>姓名</Th>
            <Th>公司</Th>
            <Th>邮箱</Th>
            <Th>电话</Th>
            <Th>主题</Th>
            <Th className="min-w-[16rem]">留言</Th>
            <Th className="w-20">状态</Th>
            <Th className="w-40">操作</Th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req) => (
            <Row
              key={req.id}
              req={req}
              busy={busyIds.has(req.id)}
              onToggle={onToggle}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th scope="col" className={`px-4 py-3 font-medium ${className}`}>
      {children}
    </th>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // YYYY-MM-DD HH:mm in the operator's local time.
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Row({
  req,
  busy,
  onToggle,
  onDelete,
}: {
  req: AdminRequest;
  busy: boolean;
  onToggle: (req: AdminRequest) => void;
  onDelete: (req: AdminRequest) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const LONG = 80;
  const isLong = req.message.length > LONG;
  const shown = expanded || !isLong ? req.message : `${req.message.slice(0, LONG)}…`;

  return (
    <tr className="border-b border-[var(--brand-tint)]/60 align-top last:border-0 hover:bg-[var(--brand-tint-3)]/30">
      <td className="whitespace-nowrap px-4 py-3 text-[var(--brand-slate-text)]">{formatDate(req.createdAt)}</td>
      <td className="px-4 py-3 font-medium text-[var(--ink)]">{req.name}</td>
      <td className="px-4 py-3 text-[var(--ink)]">{req.company || "—"}</td>
      <td className="px-4 py-3">
        <a
          href={`mailto:${req.email}`}
          className="text-[var(--brand-azure-text)] underline-offset-2 hover:underline"
        >
          {req.email}
        </a>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-[var(--ink)]">{req.phone || "—"}</td>
      <td className="px-4 py-3 text-[var(--ink)]">{req.subject || "—"}</td>
      <td className="px-4 py-3 text-[var(--ink)]">
        <p className="max-w-md whitespace-pre-wrap break-words leading-relaxed">{shown}</p>
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-[var(--text-xs)] font-medium text-[var(--brand-azure-text)] hover:underline"
          >
            {expanded ? "收起" : "展开"}
          </button>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className={[
            "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[var(--text-xs)] font-medium",
            req.handled
              ? "bg-[var(--brand-tint)] text-[var(--brand-azure-text)]"
              : "bg-amber-100 text-amber-800",
          ].join(" ")}
        >
          {req.handled ? "已处理" : "未处理"}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className={GHOST_BTN}
            disabled={busy}
            onClick={() => onToggle(req)}
          >
            {busy && <Spinner className="-ml-0.5 mr-1.5 h-3.5 w-3.5" />}
            {req.handled ? "取消" : "标记已处理"}
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-[var(--radius-md)] border border-red-200 bg-white px-4 py-2 text-[var(--text-sm)] font-medium text-red-600 transition-colors hover:border-red-400 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={busy}
            onClick={() => onDelete(req)}
          >
            删除
          </button>
        </div>
      </td>
    </tr>
  );
}

// ===========================================================================
// LoginAuditView — 登录记录: durable admin-login attempt audit (security view).
//
// Reads GET /api/admin/login-audit (paginated, newest-first). Answers "who logged
// in, from where, and were there failed/locked attempts?". Geo is the Cloudflare-
// supplied country/region/city captured at login time (null => not via the CDN, so
// untrusted/unknown). The country filter + 排除 toggle expose the "logins NOT from
// <country>" query directly (e.g. US + 排除 => every login outside the US).
// ===========================================================================
const AUDIT_FILTERS: { value: LoginAuditFilter; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "success", label: "成功" },
  { value: "failure", label: "失败" },
  { value: "locked", label: "已锁定" },
];

const AUDIT_OUTCOME_META: Record<LoginAuditOutcome, { label: string; badge: string }> = {
  success: { label: "成功", badge: "bg-green-100 text-green-800" },
  failure: { label: "失败", badge: "bg-amber-100 text-amber-800" },
  locked: { label: "已锁定", badge: "bg-red-100 text-red-700" },
};

function LoginAuditView({ onSignedOut }: { onSignedOut: () => void }) {
  const [outcome, setOutcome] = useState<LoginAuditFilter>("all");
  const [events, setEvents] = useState<LoginAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // Country filter ("US") + exclude toggle ("logins NOT from that country").
  const [country, setCountry] = useState("");
  const [exclude, setExclude] = useState(false);
  const countryQ = useDebounced(country.trim().toUpperCase(), 350);
  // A 2-letter code is the only value the server accepts; flag a partial entry so
  // the operator knows why nothing filtered (rather than silently ignoring it).
  const countryActive = /^[A-Z]{2}$/.test(countryQ);
  const countryPartial = countryQ.length > 0 && !countryActive;

  // Reset to page 1 whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [outcome, countryQ, exclude]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { status: code, data, networkError } = await listLoginAudit({
      outcome,
      country: countryActive ? countryQ : undefined,
      exclude,
      page,
      pageSize: PAGE_SIZE,
    });
    if (networkError) {
      setError("网络连接失败，请稍后重试");
      setLoading(false);
      return;
    }
    if (code === 401) {
      onSignedOut();
      return;
    }
    if (code === 503) {
      setError("管理后台未启用");
      setLoading(false);
      return;
    }
    if (code !== 200) {
      setError("加载失败，请稍后重试");
      setLoading(false);
      return;
    }
    const list = Array.isArray(data.events) ? data.events : [];
    setEvents(list);
    setTotal(typeof data.total === "number" ? data.total : list.length);
    setLoading(false);
  }, [outcome, countryActive, countryQ, exclude, page, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-8">
      {/* Outcome filter + country filter + count + refresh */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white p-1" role="tablist">
            {AUDIT_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="tab"
                aria-selected={outcome === f.value}
                onClick={() => setOutcome(f.value)}
                className={[
                  "rounded-[var(--radius-sm)] px-4 py-1.5 text-[var(--text-sm)] font-medium transition-colors",
                  outcome === f.value
                    ? "bg-[var(--brand-primary)] text-white"
                    : "text-[var(--brand-slate-text)] hover:text-[var(--brand-azure-text)]",
                ].join(" ")}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="text"
              maxLength={2}
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="国家代码 如 US"
              aria-label="按国家/地区代码筛选（两位 ISO 代码，如 US 美国、CN 中国）"
              className="w-36 rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white px-3 py-2 text-[var(--text-sm)] uppercase text-[var(--ink)] placeholder:normal-case placeholder:text-[var(--brand-slate-text)] focus:outline-none focus-visible:border-[var(--brand-azure)] focus-visible:ring-2 focus-visible:ring-[var(--brand-tint)]"
            />
            <label className="inline-flex items-center gap-1.5 text-[var(--text-sm)] text-[var(--brand-slate-text)]">
              <input
                type="checkbox"
                checked={exclude}
                onChange={(e) => setExclude(e.target.checked)}
                className="h-4 w-4 rounded border-[var(--brand-tint-2)] text-[var(--brand-primary)] focus:ring-[var(--brand-tint)]"
              />
              排除（看其他国家/地区）
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[var(--text-sm)] text-[var(--brand-slate-text)]">
            共 {formatCount(total)} 条
          </span>
          <button type="button" className={GHOST_BTN} onClick={() => void load()} disabled={loading}>
            {loading && <Spinner className="-ml-0.5 mr-2 h-3.5 w-3.5" />}
            刷新
          </button>
        </div>
      </div>

      {/* Provenance note: geo is approximate + only present for CDN traffic. */}
      <p className="mb-5 text-[var(--text-xs)] leading-relaxed text-[var(--brand-slate-text)]">
        登录地理位置由 Cloudflare 边缘提供，为近似值；未经 CDN 的访问显示为「未知」。
        {countryPartial && (
          <span className="ml-1 font-medium text-amber-700">
            （国家代码需为两位字母，如 US / CN，当前输入已忽略）
          </span>
        )}
      </p>

      {error && (
        <p
          className="mb-5 rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-4 py-3 text-[var(--text-sm)] font-medium text-red-700"
          role="alert"
        >
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-[var(--brand-slate-text)]">
          <Spinner className="h-5 w-5" />
          <span className="text-[var(--text-sm)]">加载中…</span>
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-center">
          <p className="text-[var(--text-base)] font-medium text-[var(--ink)]">暂无登录记录</p>
          <p className="mt-1 text-[var(--text-sm)] text-[var(--brand-slate-text)]">
            {outcome === "all" && !countryActive
              ? "尚未记录到任何登录尝试。"
              : "当前筛选条件下没有记录。"}
          </p>
        </div>
      ) : (
        <>
          <LoginAuditTable events={events} />
          <Pager page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} disabled={loading} />
        </>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// LoginAuditTable — read-only table of login attempts (time / outcome / user /
// country / city / device). No row actions (audit is append-only evidence).
// ---------------------------------------------------------------------------
function LoginAuditTable({ events }: { events: LoginAuditEvent[] }) {
  return (
    <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white shadow-[var(--shadow-card)]">
      <table className="w-full min-w-[52rem] border-collapse text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/50 text-[var(--brand-slate-text)]">
            <Th className="w-40">时间</Th>
            <Th className="w-24">结果</Th>
            <Th className="w-28">用户名</Th>
            <Th className="w-44">国家 / 地区</Th>
            <Th className="w-32">城市</Th>
            <Th className="min-w-[14rem]">设备 (User-Agent)</Th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => {
            const meta = AUDIT_OUTCOME_META[ev.outcome];
            const countryName = ev.country ? countryLabel(ev.country) : "未知";
            const regionName = ev.region ? regionLabel(ev.region) : "";
            const cityName = ev.city ? cityLabel(ev.city) : "—";
            return (
              <tr
                key={ev.id}
                className="border-b border-[var(--brand-tint)]/60 align-top last:border-0 hover:bg-[var(--brand-tint-3)]/30"
              >
                <td className="whitespace-nowrap px-4 py-3 text-[var(--brand-slate-text)]">
                  {ev.createdAt ? formatDate(ev.createdAt) : "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={[
                      "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[var(--text-xs)] font-medium",
                      meta?.badge ?? "bg-[var(--brand-tint)] text-[var(--brand-slate-text)]",
                    ].join(" ")}
                  >
                    {meta?.label ?? ev.outcome}
                  </span>
                </td>
                <td className="px-4 py-3 text-[var(--ink)]">{ev.username || "—"}</td>
                <td className="px-4 py-3 text-[var(--ink)]">
                  <span className="font-medium">{countryName}</span>
                  {regionName && (
                    <span className="ml-1.5 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
                      {regionName}
                    </span>
                  )}
                  {ev.country && (
                    <span className="ml-1.5 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
                      ({ev.country})
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-[var(--ink)]">{cityName}</td>
                <td className="px-4 py-3 text-[var(--brand-slate-text)]">
                  <span className="block max-w-md truncate" title={ev.userAgent ?? undefined}>
                    {ev.userAgent || "—"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// IssuesView — website-maintenance issue tracker (问题跟踪 tab).
// ===========================================================================
const ISSUE_FILTERS: { value: IssueFilter; label: string }[] = [
  { value: "all", label: "全部" },
  { value: "open", label: "待处理" },
  { value: "fixed", label: "已修复" },
  { value: "closed", label: "已关闭" },
];

const ISSUE_STATUS_META: Record<IssueStatus, { label: string; badge: string }> = {
  open: { label: "待处理", badge: "bg-amber-100 text-amber-800" },
  fixed: { label: "已修复", badge: "bg-green-100 text-green-800" },
  closed: { label: "已关闭", badge: "bg-[var(--brand-tint)] text-[var(--brand-slate-text)]" },
};

function IssuesView({ onSignedOut }: { onSignedOut: () => void }) {
  const [filter, setFilter] = useState<IssueFilter>("all");
  const [issues, setIssues] = useState<AdminIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // search (debounced) + pagination state.
  const [search, setSearch] = useState("");
  const q = useDebounced(search, 350);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  // IDs with an in-flight row action (status change / save remark / delete).
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const setBusy = useCallback((id: string, busy: boolean) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  // Reset to page 1 whenever the filter or the (debounced) query changes.
  useEffect(() => {
    setPage(1);
  }, [filter, q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { status: code, data, networkError } = await listIssues(filter, {
      page,
      pageSize: PAGE_SIZE,
      q,
    });
    if (networkError) {
      setError("网络连接失败，请稍后重试");
      setLoading(false);
      return;
    }
    if (code === 401) {
      onSignedOut();
      return;
    }
    if (code === 503) {
      setError("管理后台未启用");
      setLoading(false);
      return;
    }
    if (code !== 200) {
      setError("加载失败，请稍后重试");
      setLoading(false);
      return;
    }
    const list = (Array.isArray(data.issues) ? data.issues : []).map((it) => ({
      ...it,
      // Defensively normalise: older BFF builds may omit attachments[].
      attachments: Array.isArray(it.attachments) ? it.attachments : [],
    }));
    setIssues(list);
    setTotal(typeof data.total === "number" ? data.total : list.length);
    setLoading(false);
  }, [filter, page, q, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  // Returns true when the action succeeded (so callers can close forms etc.).
  async function handleStatus(issue: AdminIssue, status: IssueStatus): Promise<boolean> {
    setBusy(issue.id, true);
    try {
      const { status: code, networkError } = await updateIssue(issue.id, { status });
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return false;
      }
      if (code === 401) {
        onSignedOut();
        return false;
      }
      if (code !== 200) {
        setError("操作失败，请稍后重试");
        return false;
      }
      await load();
      return true;
    } finally {
      setBusy(issue.id, false);
    }
  }

  async function handleSaveRemark(issue: AdminIssue, remark: string): Promise<boolean> {
    setBusy(issue.id, true);
    try {
      const { status: code, networkError } = await updateIssue(issue.id, { remark });
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return false;
      }
      if (code === 401) {
        onSignedOut();
        return false;
      }
      if (code !== 200) {
        setError("保存失败，请稍后重试");
        return false;
      }
      await load();
      return true;
    } finally {
      setBusy(issue.id, false);
    }
  }

  async function handleDelete(issue: AdminIssue) {
    if (
      !confirmDelete(
        `确定删除问题「${issue.title}」(${issue.code}) 吗？删除后将不再显示（如需恢复请联系开发）。`,
      )
    )
      return;
    setBusy(issue.id, true);
    try {
      const { status: code, networkError } = await deleteIssue(issue.id);
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return;
      }
      if (code === 401) {
        onSignedOut();
        return;
      }
      if (code !== 200) {
        setError("删除失败，请稍后重试");
        return;
      }
      if (issues.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } finally {
      setBusy(issue.id, false);
    }
  }

  // --- attachments (B) -------------------------------------------------------
  /** Upload one image to an existing issue, then refresh. Returns success. */
  async function handleUploadAttachment(issue: AdminIssue, file: File): Promise<boolean> {
    setBusy(issue.id, true);
    try {
      const { status: code, networkError } = await uploadIssueAttachment(issue.id, file);
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return false;
      }
      if (code === 401) {
        onSignedOut();
        return false;
      }
      if (code === 503) {
        setError("图片上传未启用");
        return false;
      }
      if (code === 413) {
        setError("图片过大（单张需 ≤ 3MB）");
        return false;
      }
      if (code === 415) {
        setError("不支持的图片类型（仅支持 PNG / JPEG / WebP / GIF）");
        return false;
      }
      if (code === 409) {
        setError(`每个问题最多 ${ATTACHMENT_MAX_COUNT} 张图片`);
        return false;
      }
      if (code !== 201 && code !== 200) {
        setError("图片上传失败，请稍后重试");
        return false;
      }
      await load();
      return true;
    } finally {
      setBusy(issue.id, false);
    }
  }

  /** Delete one attachment (with confirm), then refresh. */
  async function handleDeleteAttachment(issue: AdminIssue, att: IssueAttachment) {
    if (
      !confirmDelete(
        `确定删除图片「${att.filename}」吗？删除后将不再显示（如需恢复请联系开发）。`,
      )
    )
      return;
    setBusy(issue.id, true);
    try {
      const { status: code, networkError } = await deleteIssueAttachment(att.id);
      if (networkError) {
        setError("网络连接失败，请稍后重试");
        return;
      }
      if (code === 401) {
        onSignedOut();
        return;
      }
      if (code === 503) {
        setError("图片功能未启用");
        return;
      }
      if (code !== 200) {
        setError("删除图片失败，请稍后重试");
        return;
      }
      await load();
    } finally {
      setBusy(issue.id, false);
    }
  }

  // Create flow — owned here so we can refresh + close on success. When an
  // (optional) image is attached, we create the issue FIRST, then upload to the
  // returned issue id. A failed upload doesn't undo the created issue: we surface
  // a non-blocking warning so the operator can retry from the row.
  async function handleCreate(input: {
    title: string;
    description: string;
    remark: string;
    file: File | null;
  }): Promise<boolean> {
    const body = {
      title: input.title.trim(),
      ...(input.description.trim() ? { description: input.description.trim() } : {}),
      ...(input.remark.trim() ? { remark: input.remark.trim() } : {}),
    };
    const { status: code, data, networkError } = await createIssue(body);
    if (networkError) {
      setError("网络连接失败，请稍后重试");
      return false;
    }
    if (code === 401) {
      onSignedOut();
      return false;
    }
    if (code === 503) {
      setError("管理后台未启用");
      return false;
    }
    if (code !== 201 && code !== 200) {
      setError("创建失败，请稍后重试");
      return false;
    }

    // Optional image: upload to the freshly-created issue.
    if (input.file && data.issue?.id) {
      const up = await uploadIssueAttachment(data.issue.id, input.file);
      if (up.status === 401) {
        onSignedOut();
        return false;
      }
      if (up.networkError || (up.status !== 201 && up.status !== 200)) {
        // Issue was created; only the image failed. Keep the form open is not
        // needed — surface a hint and continue.
        setError(
          up.status === 503
            ? "问题已创建，但图片上传未启用。"
            : "问题已创建，但图片上传失败，可在列表中重试。",
        );
      }
    }

    setCreating(false);
    await load();
    return true;
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-8">
      {/* New-issue button + filter + count + refresh */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className={PRIMARY_BTN}
            onClick={() => setCreating((v) => !v)}
          >
            {creating ? "取消新建" : "新建问题"}
          </button>
          <div
            className="inline-flex rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white p-1"
            role="tablist"
          >
            {ISSUE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="tab"
                aria-selected={filter === f.value}
                onClick={() => setFilter(f.value)}
                className={[
                  "rounded-[var(--radius-sm)] px-4 py-1.5 text-[var(--text-sm)] font-medium transition-colors",
                  filter === f.value
                    ? "bg-[var(--brand-primary)] text-white"
                    : "text-[var(--brand-slate-text)] hover:text-[var(--brand-azure-text)]",
                ].join(" ")}
              >
                {f.label}
              </button>
            ))}
          </div>
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="搜索编号 / 标题 / 描述 / 备注"
          />
        </div>

        <div className="flex items-center gap-3">
          <span className="text-[var(--text-sm)] text-[var(--brand-slate-text)]">共 {formatCount(total)} 条</span>
          <button type="button" className={GHOST_BTN} onClick={() => void load()} disabled={loading}>
            {loading && <Spinner className="-ml-0.5 mr-2 h-3.5 w-3.5" />}
            刷新
          </button>
        </div>
      </div>

      {creating && <NewIssueForm onCreate={handleCreate} onCancel={() => setCreating(false)} />}

      {error && (
        <p
          className="mb-5 rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-4 py-3 text-[var(--text-sm)] font-medium text-red-700"
          role="alert"
        >
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-[var(--brand-slate-text)]">
          <Spinner className="h-5 w-5" />
          <span className="text-[var(--text-sm)]">加载中…</span>
        </div>
      ) : issues.length === 0 ? (
        <div className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white py-16 text-center">
          <p className="text-[var(--text-base)] font-medium text-[var(--ink)]">暂无问题</p>
          <p className="mt-1 text-[var(--text-sm)] text-[var(--brand-slate-text)]">
            {q
              ? "没有匹配搜索条件的问题。"
              : filter === "all"
                ? "还没有记录任何问题。"
                : "当前筛选条件下没有记录。"}
          </p>
        </div>
      ) : (
        <>
          <IssueTable
            issues={issues}
            busyIds={busyIds}
            onStatus={handleStatus}
            onSaveRemark={handleSaveRemark}
            onDelete={handleDelete}
            onUploadAttachment={handleUploadAttachment}
            onDeleteAttachment={handleDeleteAttachment}
            onSignedOut={onSignedOut}
          />
          <Pager
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPage={setPage}
            disabled={loading}
          />
        </>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// NewIssueForm — title* + description + remark + an OPTIONAL image, posts via
// onCreate (the parent creates the issue, then uploads the file to it).
// ---------------------------------------------------------------------------
function NewIssueForm({
  onCreate,
  onCancel,
}: {
  onCreate: (input: {
    title: string;
    description: string;
    remark: string;
    file: File | null;
  }) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [remark, setRemark] = useState("");
  const [file, setFile] = useState<File | null>(null);
  // Local preview object URL for the chosen image; revoked on change/unmount.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Keep the preview object URL in sync with the chosen file; always revoke.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) {
      setFile(null);
      return;
    }
    const err = validateImageFile(picked);
    if (err) {
      setLocalError(err);
      setFile(null);
      // Reset the native input so re-picking the same bad file re-fires change.
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setLocalError(null);
    setFile(picked);
  }

  function clearFile() {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const trimmed = title.trim();
    if (!trimmed) {
      setLocalError("请填写标题");
      return;
    }
    if (trimmed.length > 200) {
      setLocalError("标题不能超过 200 字");
      return;
    }
    if (description.length > 4000) {
      setLocalError("描述不能超过 4000 字");
      return;
    }
    if (remark.length > 2000) {
      setLocalError("备注不能超过 2000 字");
      return;
    }
    if (file) {
      const err = validateImageFile(file);
      if (err) {
        setLocalError(err);
        return;
      }
    }
    setLocalError(null);
    setPending(true);
    try {
      const ok = await onCreate({ title, description, remark, file });
      if (ok) {
        setTitle("");
        setDescription("");
        setRemark("");
        clearFile();
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="mb-5 rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6 shadow-[var(--shadow-card)]"
    >
      <h2 className="font-zh text-[var(--text-base)] font-semibold text-[var(--ink)]">新建问题</h2>
      <p className="mt-1 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
        问题编号由系统自动生成。
      </p>

      {localError && (
        <p
          className="mt-4 rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-4 py-2.5 text-[var(--text-sm)] font-medium text-red-700"
          role="alert"
        >
          {localError}
        </p>
      )}

      <div className="mt-4 space-y-4">
        <div>
          <label htmlFor="issue-title" className="mb-1.5 block text-[var(--text-sm)] font-medium text-[var(--ink)]">
            标题 <span className="text-red-600">*</span>
          </label>
          <input
            id="issue-title"
            type="text"
            maxLength={200}
            className={INPUT_CLASS}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        <div>
          <label htmlFor="issue-desc" className="mb-1.5 block text-[var(--text-sm)] font-medium text-[var(--ink)]">
            描述
          </label>
          <textarea
            id="issue-desc"
            rows={3}
            maxLength={4000}
            className={`${INPUT_CLASS} resize-y`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div>
          <label htmlFor="issue-remark" className="mb-1.5 block text-[var(--text-sm)] font-medium text-[var(--ink)]">
            备注
          </label>
          <textarea
            id="issue-remark"
            rows={2}
            maxLength={2000}
            className={`${INPUT_CLASS} resize-y`}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </div>

        {/* Optional image / screenshot. */}
        <div>
          <label htmlFor="issue-image" className="mb-1.5 block text-[var(--text-sm)] font-medium text-[var(--ink)]">
            添加图片 / 截图（可选）
          </label>
          <input
            id="issue-image"
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={onPickFile}
            className="block w-full text-[var(--text-sm)] text-[var(--brand-slate-text)] file:mr-3 file:rounded-[var(--radius-md)] file:border-0 file:bg-[var(--brand-tint-3)] file:px-4 file:py-2 file:text-[var(--text-sm)] file:font-medium file:text-[var(--brand-azure-text)] hover:file:bg-[var(--brand-tint)]"
          />
          <p className="mt-1 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
            支持 PNG / JPEG / WebP / GIF，单张 ≤ 3MB。
          </p>
          {file && previewUrl && (
            <div className="mt-3 flex items-start gap-3">
              <img
                src={previewUrl}
                alt="预览"
                className="h-24 w-24 rounded-[var(--radius-md)] border border-[var(--brand-tint)] object-cover"
              />
              <div className="min-w-0 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
                <p className="truncate font-medium text-[var(--ink)]">{file.name}</p>
                <p className="mt-0.5">{formatBytes(file.size)}</p>
                <button
                  type="button"
                  onClick={clearFile}
                  className="mt-1.5 font-medium text-red-600 hover:underline"
                >
                  移除图片
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button type="submit" className={PRIMARY_BTN} disabled={pending}>
          {pending && <Spinner className="-ml-1 mr-2.5 h-4 w-4" />}
          {pending ? "提交中…" : "创建"}
        </button>
        <button type="button" className={GHOST_BTN} onClick={onCancel} disabled={pending}>
          取消
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// IssueTable — responsive table of issues with per-row actions.
// ---------------------------------------------------------------------------
interface AttachmentHandlers {
  onUploadAttachment: (issue: AdminIssue, file: File) => Promise<boolean>;
  onDeleteAttachment: (issue: AdminIssue, att: IssueAttachment) => void;
  onSignedOut: () => void;
}

function IssueTable({
  issues,
  busyIds,
  onStatus,
  onSaveRemark,
  onDelete,
  onUploadAttachment,
  onDeleteAttachment,
  onSignedOut,
}: {
  issues: AdminIssue[];
  busyIds: Set<string>;
  onStatus: (issue: AdminIssue, status: IssueStatus) => Promise<boolean>;
  onSaveRemark: (issue: AdminIssue, remark: string) => Promise<boolean>;
  onDelete: (issue: AdminIssue) => void;
} & AttachmentHandlers) {
  return (
    <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white shadow-[var(--shadow-card)]">
      <table className="w-full min-w-[72rem] border-collapse text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/50 text-[var(--brand-slate-text)]">
            <Th className="w-32">编号</Th>
            <Th className="min-w-[12rem]">标题</Th>
            <Th className="min-w-[18rem]">描述 / 备注</Th>
            <Th className="w-48">图片</Th>
            <Th className="w-20">状态</Th>
            <Th className="w-36">更新时间</Th>
            <Th className="w-44">操作</Th>
          </tr>
        </thead>
        <tbody>
          {issues.map((issue) => (
            <IssueRow
              key={issue.id}
              issue={issue}
              busy={busyIds.has(issue.id)}
              onStatus={onStatus}
              onSaveRemark={onSaveRemark}
              onDelete={onDelete}
              onUploadAttachment={onUploadAttachment}
              onDeleteAttachment={onDeleteAttachment}
              onSignedOut={onSignedOut}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IssueRow({
  issue,
  busy,
  onStatus,
  onSaveRemark,
  onDelete,
  onUploadAttachment,
  onDeleteAttachment,
  onSignedOut,
}: {
  issue: AdminIssue;
  busy: boolean;
  onStatus: (issue: AdminIssue, status: IssueStatus) => Promise<boolean>;
  onSaveRemark: (issue: AdminIssue, remark: string) => Promise<boolean>;
  onDelete: (issue: AdminIssue) => void;
} & AttachmentHandlers) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [remarkDraft, setRemarkDraft] = useState(issue.remark ?? "");

  const meta = ISSUE_STATUS_META[issue.status];
  const description = issue.description ?? "";
  const LONG = 100;
  const isLong = description.length > LONG;
  const shownDesc = expanded || !isLong ? description : `${description.slice(0, LONG)}…`;

  async function saveRemark() {
    const ok = await onSaveRemark(issue, remarkDraft.trim());
    if (ok) setEditing(false);
  }

  return (
    <tr className="border-b border-[var(--brand-tint)]/60 align-top last:border-0 hover:bg-[var(--brand-tint-3)]/30">
      <td className="px-4 py-3">
        <span className="whitespace-nowrap font-mono text-[var(--text-xs)] font-medium text-[var(--ink)]">
          {issue.code}
        </span>
      </td>
      <td className="px-4 py-3 font-medium text-[var(--ink)]">
        <p className="max-w-xs break-words leading-relaxed">{issue.title}</p>
        {issue.attachments.length > 0 && (
          <span className="mt-1 inline-flex items-center gap-1 text-[var(--text-xs)] font-normal text-[var(--brand-slate-text)]">
            📎 {issue.attachments.length}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-[var(--ink)]">
        {description ? (
          <p className="max-w-md whitespace-pre-wrap break-words leading-relaxed">{shownDesc}</p>
        ) : (
          <p className="text-[var(--brand-slate-text)]">—</p>
        )}
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-[var(--text-xs)] font-medium text-[var(--brand-azure-text)] hover:underline"
          >
            {expanded ? "收起" : "展开"}
          </button>
        )}

        {/* Remark — inline editable. */}
        <div className="mt-2 border-t border-[var(--brand-tint)]/60 pt-2">
          {editing ? (
            <div>
              <textarea
                rows={2}
                maxLength={2000}
                className={`${INPUT_CLASS} resize-y text-[var(--text-sm)]`}
                value={remarkDraft}
                onChange={(e) => setRemarkDraft(e.target.value)}
                placeholder="备注"
              />
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  className={GHOST_BTN}
                  disabled={busy}
                  onClick={() => void saveRemark()}
                >
                  {busy && <Spinner className="-ml-0.5 mr-1.5 h-3.5 w-3.5" />}
                  保存
                </button>
                <button
                  type="button"
                  className={GHOST_BTN}
                  disabled={busy}
                  onClick={() => {
                    setRemarkDraft(issue.remark ?? "");
                    setEditing(false);
                  }}
                >
                  取消
                </button>
              </div>
            </div>
          ) : (
            <p className="text-[var(--text-xs)] text-[var(--brand-slate-text)]">
              <span className="font-medium">备注：</span>
              {issue.remark ? (
                <span className="whitespace-pre-wrap break-words">{issue.remark}</span>
              ) : (
                "—"
              )}
            </p>
          )}
        </div>
      </td>
      <td className="px-4 py-3 align-top">
        <AttachmentCell
          issue={issue}
          busy={busy}
          onUploadAttachment={onUploadAttachment}
          onDeleteAttachment={onDeleteAttachment}
          onSignedOut={onSignedOut}
        />
      </td>
      <td className="px-4 py-3">
        <span
          className={[
            "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-[var(--text-xs)] font-medium",
            meta.badge,
          ].join(" ")}
        >
          {meta.label}
        </span>
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-[var(--brand-slate-text)]">{formatDate(issue.updatedAt)}</td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-2">
          {issue.status !== "fixed" && (
            <button
              type="button"
              className={GHOST_BTN}
              disabled={busy}
              onClick={() => void onStatus(issue, "fixed")}
            >
              {busy && <Spinner className="-ml-0.5 mr-1.5 h-3.5 w-3.5" />}
              标记已修复
            </button>
          )}
          {issue.status !== "closed" && (
            <button
              type="button"
              className={GHOST_BTN}
              disabled={busy}
              onClick={() => void onStatus(issue, "closed")}
            >
              关闭
            </button>
          )}
          {issue.status !== "open" && (
            <button
              type="button"
              className={GHOST_BTN}
              disabled={busy}
              onClick={() => void onStatus(issue, "open")}
            >
              重新打开
            </button>
          )}
          {!editing && (
            <button
              type="button"
              className={GHOST_BTN}
              disabled={busy}
              onClick={() => {
                setRemarkDraft(issue.remark ?? "");
                setEditing(true);
              }}
            >
              编辑备注
            </button>
          )}
          <button
            type="button"
            className={DANGER_BTN}
            disabled={busy}
            onClick={() => onDelete(issue)}
          >
            删除
          </button>
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// AttachmentCell — per-issue thumbnails (loaded via authed objectURL), a click-
// to-open lightbox, an "add image" control (gated by the per-issue max), and a
// per-thumbnail delete. Each thumbnail revokes its objectURL on unmount.
// ---------------------------------------------------------------------------
function AttachmentCell({
  issue,
  busy,
  onUploadAttachment,
  onDeleteAttachment,
  onSignedOut,
}: {
  issue: AdminIssue;
  busy: boolean;
} & AttachmentHandlers) {
  const attachments = issue.attachments;
  const [lightbox, setLightbox] = useState<{ url: string; name: string } | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const atMax = attachments.length >= ATTACHMENT_MAX_COUNT;

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    if (fileInputRef.current) fileInputRef.current.value = ""; // allow re-pick
    if (!picked) return;
    const err = validateImageFile(picked);
    if (err) {
      setLocalError(err);
      return;
    }
    setLocalError(null);
    await onUploadAttachment(issue, picked);
  }

  return (
    <div>
      {attachments.length === 0 ? (
        <p className="text-[var(--text-xs)] text-[var(--brand-slate-text)]">暂无图片</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {attachments.map((att) => (
            <AttachmentThumb
              key={att.id}
              att={att}
              busy={busy}
              onOpen={(url) => setLightbox({ url, name: att.filename })}
              onDelete={() => onDeleteAttachment(issue, att)}
              onSignedOut={onSignedOut}
            />
          ))}
        </div>
      )}

      {/* Add another image (hidden native input + a small button). */}
      <div className="mt-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={onPickFile}
          className="hidden"
          id={`att-add-${issue.id}`}
        />
        <button
          type="button"
          className="text-[var(--text-xs)] font-medium text-[var(--brand-azure-text)] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy || atMax}
          onClick={() => fileInputRef.current?.click()}
        >
          {busy ? "处理中…" : atMax ? `已达上限（${ATTACHMENT_MAX_COUNT} 张）` : "+ 添加图片"}
        </button>
        {localError && (
          <p className="mt-1 text-[var(--text-xs)] font-medium text-red-600">{localError}</p>
        )}
      </div>

      {lightbox && (
        <Lightbox url={lightbox.url} name={lightbox.name} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

// One thumbnail. Fetches the object bytes (authed) -> objectURL, shows a spinner
// while loading and a fallback on failure. Revokes the URL on unmount.
function AttachmentThumb({
  att,
  busy,
  onOpen,
  onDelete,
  onSignedOut,
}: {
  att: IssueAttachment;
  busy: boolean;
  onOpen: (objectUrl: string) => void;
  onDelete: () => void;
  onSignedOut: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let created: string | null = null;
    void (async () => {
      const { status, objectUrl } = await fetchAttachmentObjectUrl(att.id);
      if (!active) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        return;
      }
      if (status === 401) {
        onSignedOut();
        return;
      }
      if (!objectUrl) {
        setFailed(true);
        return;
      }
      created = objectUrl;
      setUrl(objectUrl);
    })();
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [att.id, onSignedOut]);

  return (
    <div className="group relative h-16 w-16">
      {url ? (
        <button
          type="button"
          onClick={() => onOpen(url)}
          className="block h-16 w-16 overflow-hidden rounded-[var(--radius-md)] border border-[var(--brand-tint)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brand-azure)]"
          title={`${att.filename} · ${formatBytes(att.byteSize)}`}
        >
          <img src={url} alt={att.filename} className="h-full w-full object-cover" />
        </button>
      ) : failed ? (
        <div className="flex h-16 w-16 items-center justify-center rounded-[var(--radius-md)] border border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/40 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
          加载失败
        </div>
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-[var(--radius-md)] border border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/40 text-[var(--brand-slate-text)]">
          <Spinner className="h-4 w-4" />
        </div>
      )}
      {/* Delete (×) overlay — appears on hover/focus. */}
      <button
        type="button"
        disabled={busy}
        onClick={onDelete}
        aria-label={`删除图片 ${att.filename}`}
        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 opacity-0 shadow-sm transition-opacity hover:bg-red-50 focus:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

// Lightbox — full-image overlay; click backdrop or × or Esc to close.
function Lightbox({
  url,
  name,
  onClose,
}: {
  url: string;
  name: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={name}
      onClick={onClose}
    >
      <div className="relative max-h-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <img
          src={url}
          alt={name}
          className="max-h-[85vh] max-w-full rounded-[var(--radius-md)] object-contain shadow-2xl"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭"
          className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[var(--ink)] shadow-lg hover:bg-[var(--brand-tint-3)]"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
}
